// The condition reports made for a visit (#513), each kept as made (#502):
// open it, make a PDF copy beside it, or mark it sent to the client — which
// makes the visit Issued (only once it has been validated).
import { useState } from 'react';
import { Button, Label, OutboundLink } from '@higgins/ui';
import { ApiError, isConnectionFailure, makeReportPdfApi, markReportSentApi, type IssuedReportFromApi } from '../lib/api';
import { takeServerChange } from '../lib/device-db';
import { openWaitingTab } from '../lib/file-handoff';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '');

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'This needs signal.';
  if (err instanceof ApiError) return err.message;
  return 'That didn’t work. Try again.';
}

export function IssuedReportList({ reports, onChanged }: { reports: IssuedReportFromApi[]; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function run(id: string, work: () => Promise<void>) {
    setBusy(id);
    setProblem(null);
    try {
      await work();
      onChanged();
    } catch (err) {
      setProblem(describe(err));
    } finally {
      setBusy(null);
    }
  }

  /** Mark sent: the visit becomes Issued on the server, and the device's copy takes it (#576 review). */
  async function markSent(reportId: string) {
    const sent = await markReportSentApi(reportId);
    await takeServerChange(sent.visitId, sent.visit.fields, sent.visit.etag);
  }

  const pdf = (r: IssuedReportFromApi) => {
    // The tab opens on the tap, or the pop-up blocker stops it (root CLAUDE.md).
    const tab = openWaitingTab('Making the PDF…');
    return run(r.id, async () => {
      try {
        const made = await makeReportPdfApi(r.id);
        if (tab) tab.show(made.webUrl);
      } catch (err) {
        tab?.close();
        throw err;
      }
    });
  };

  return (
    <div>
      <Label>Made so far</Label>
      <ul className="mt-1 divide-y divide-slate-100 text-sm">
        {reports.map((r) => (
          <li key={r.id || r.name} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
            <span className="min-w-0">
              {r.url ? (
                <OutboundLink openIn="edge" href={r.url} className="text-primary underline">
                  {r.name}
                </OutboundLink>
              ) : (
                <span>{r.name}</span>
              )}
              <span className="block text-xs text-slate-500">
                {when(r.issuedOn)}
                {r.issuedBy ? ` · ${r.issuedBy}` : ''}
                {r.sentOn ? ` · sent ${when(r.sentOn)}` : ''}
              </span>
            </span>
            <span className="flex gap-2">
              {r.pdfUrl ? (
                <OutboundLink openIn="edge" href={r.pdfUrl} className="inline-flex min-h-11 items-center text-sm text-primary underline">
                  PDF
                </OutboundLink>
              ) : (
                <Button variant="outline" size="sm" className="min-h-11" disabled={busy !== null || !r.id} onClick={() => void pdf(r)}>
                  Make PDF
                </Button>
              )}
              {!r.sentOn && (
                <Button variant="outline" size="sm" className="min-h-11" disabled={busy !== null || !r.id} onClick={() => void run(r.id, () => markSent(r.id))}>
                  Mark sent
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {problem && <p className="mt-1 text-sm text-red-700">{problem}</p>}
    </div>
  );
}
